import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganizationsAggregateTotals } from './hooks/useOrganizations';
import PageHeader from './components/PageHeader';
import IconButton from './components/IconButton';
import EditActionIcon from './components/EditActionIcon';
import Pagination from './components/Pagination';
import SortableTableHeader from './components/SortableTableHeader';
import { useUrlFilters } from './hooks/urlParams';
import type { OrganizationRecord } from './types/index';
import { DEFAULT_PAGE_SIZE } from './constants';
import { getAvailableActions, getWriteActions } from './utils/permissionCatalog';
import { checkPermissionActions } from './utils/permissions';
import { getNextSortCriterion, type FieldSortCriterionValue } from './utils/tableSort';

export default function OrganizationsPage({ onAdd, onEdit, isSuperUser: _isSuperUser, currentUser }) {
  const navigate = useNavigate();
  const actions = getAvailableActions(currentUser);
  const canWriteOrganizations = checkPermissionActions(actions, getWriteActions('OrganizationManagement'));
  const descendingFirstFields = useRef(new Set(['lastModified']));
  const [filters, setFilters] = useUrlFilters<{ name: string }>({
    name: { default: '', type: 'string' },
  });
  const [results, setResults] = useState<{ page: number; pageSize: number; total: number; data: OrganizationRecord[] }>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    data: [] as OrganizationRecord[],
  });
  const [sort, setSort] = useState<FieldSortCriterionValue>({
    fieldName: 'name',
    isAscending: true,
  });

  useEffect(() => {
    let cancelled = false;
    getOrganizationsAggregateTotals({
      page: results.page,
      pageSize: results.pageSize,
      name: filters.name?.trim() || null,
      sortField: sort.fieldName,
      sortAscending: sort.isAscending,
    }).then((response) => {
      if (cancelled) return;
      const data = response.data.map((org: any) => ({
        ...org,
        market: Array.isArray(org?.markets)
          ? org.markets.map((market) => market?.name || market?.code || '').filter(Boolean).join(', ')
          : '',
        facilities: org?.facilityCounts ?? 0,
        users: org?.userCounts ?? 0,
        lastModified: org?.lastModified ? new Date(org.lastModified).toLocaleDateString() : null,
      }));
      setResults({ ...response, data });
    }).catch(() => null);
    return () => { cancelled = true; };
  }, [filters.name, results.page, results.pageSize, sort.fieldName, sort.isAscending]);

  const toggleSortDirection = useCallback((fieldName: string) => {
    setSort((current) => getNextSortCriterion(current, fieldName, descendingFirstFields.current));
    setResults((current) => ({ ...current, page: 0 }));
  }, []);

  return (
    <div className="stack" data-testid="organizations-page">
      <button className="button" type="button" onClick={() => navigate('/admin')} data-testid="organizations-back-to-admin" style={{ alignSelf: 'flex-start' }}>
        Back
      </button>
      <PageHeader
        title="Organizations"
        subtitle="Overview"
        actions={
          <div>
              {canWriteOrganizations && <button className="button cta" onClick={onAdd}>
                <span className="plus" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Add Organization
              </button>}
          </div>
        }
      />

      <div className="card filter-card">
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              type="search"
              placeholder="Search organizations"
              value={filters.name}
              onChange={(e) => {
                setResults((current) => ({ ...current, page: 0 }));
                setFilters({ name: e.target.value });
              }}
              data-testid="organizations-search"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <SortableTableHeader label="Name" fieldName="name" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Market" fieldName="marketName" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Facilities" fieldName="facilities" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Users" fieldName="users" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Last Updated" fieldName="lastModified" sort={sort} onSort={toggleSortDirection} />
              <th className="col-actions" aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {results.data.map((org: OrganizationRecord) => (
              <tr key={org.id}>
                <td>{org.name}</td>
                <td>{org.market}</td>
                <td>{org.facilities}</td>
                <td>{org.users}</td>
                <td>{org.lastModified ? String(org.lastModified) : ''}</td>
                <td className="col-actions">
                  {canWriteOrganizations && <IconButton title="Edit" ariaLabel={`Edit ${org.name || 'organization'}`} onClick={() => onEdit && onEdit(org)}>
                    <EditActionIcon />
                  </IconButton>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={results.page}
          totalPages={Math.ceil(results.total / results.pageSize)}
          onPrev={() => { setResults((current) => ({ ...current, page: current.page - 1 })) }}
          onNext={() => { setResults((current) => ({ ...current, page: current.page + 1 })) }}
          format="of"
          prevLabel="◀"
          nextLabel="▶"
        />
       </div>
    </div>
  );
}
